import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Location, NgClass, NgTemplateOutlet, NgStyle } from '@angular/common';
import { patterns, SharedService } from '../../shared.service';
import { ApiService } from '../../api.service';
import { forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { localStorageService } from '../../localstorage.service';
import { SalesViewService } from 'src/app/components/pages/sales/sales-view.service';
import { PermissionsService } from '../../permissions/permissions.service';
import { FormsModule } from '@angular/forms';
import { HighlightPipe } from '../../highlight.pipe';
@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, NgClass, RouterLink, NgTemplateOutlet, NgStyle, HighlightPipe]
})
export class SearchComponent implements OnInit,OnDestroy {
  term: string = '';
  searched_string = '';
  emptyData!: boolean;
  results: any = {
    partners: [],
    patients: [],
    doctors: [],
    contracts: [],
    payments: [],
    ar: [],
    proposal: [],
    pa_leads: [],
    training:[]
  };
  show: any = {
    doctor: true,
    contract: true,
    patient: true,
    partner: false,
    payments: false,
    ar: true,
    proposal: true,
    pa_leads: false,
    training:true,
    leads: true
  };



  getInitials(name: string): string {
    if (!name) return '';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  }

  public totalRowsOfEach:any={};
  public searchSubscription: Subscription | undefined;
  admin_profile: any;
  /** Cap-driven TL / manager flags (replace legacy role_enum allowlist). */
  isTeamLead = false;
  isManager = false
  /** Pure sales agents / team leads search only sales leads (sales/search-lead). */
  searchBySalesAgent = false;
  // Pure-tier flags derived from PermissionsService snapshot — used by
  // navigateToDetails to figure out which admin_id to forward for TL views.
  private isAgentL1Pure = false;
  private isAgentL2Pure = false;
  private isTeamLeadL1 = false;
  private isTeamLeadL2 = false;
  private permissionsDestroy$ = new Subject<void>();
  users: any[] = [];         
  filteredUsers: any[] = [];
  resultCount: number = 0;

  constructor(
    private route: ActivatedRoute,
    public location: Location,
    public router: Router,
    private _shared: SharedService,
    private _api: ApiService,
    private _local : localStorageService,
    private salesView : SalesViewService,
    private permissions: PermissionsService,
  ) {
  }
// .pipe(
//       map(params => params['search']),// map value according to search then send to next operators and subsciber
//       distinctUntilChanged(),//to avoid duplicate API calls for the same value in one row
//     )
  ngOnInit() {
    this.admin_profile = this._local.customGetItem('admin_profile');
    this.permissions.snapshot$
      .pipe(takeUntil(this.permissionsDestroy$))
      .subscribe(() => this.computePermissionFlags());
    this.computePermissionFlags();
    this.searchSubscription = this.route.queryParams.subscribe((data: any) => {
        this.term = data.search;
        this.searchData(data.search);
        this.handleSearch();
     
    });
  }

  private computePermissionFlags(): void {
    const flags = this.permissions.salesPermissionFlags;
    this.isTeamLead = flags.isTeamLead;
    this.isManager = flags.isManager;
    this.searchBySalesAgent = flags.searchBySalesAgent;
    this.isAgentL1Pure = flags.isAgentL1Pure;
    this.isAgentL2Pure = flags.isAgentL2Pure;
    this.isTeamLeadL1 = flags.isTeamLeadL1;
    this.isTeamLeadL2 = flags.isTeamLeadL2;
  }

  searchData(data: any) {
    this.term = data;
      const isPhoneNumber = patterns.some(pattern => pattern.test((this.term + '').trim()));
      if (isPhoneNumber) {
        console.log(isPhoneNumber)
        const digits = (this.term + '').match(/\d+/g);
        this.term = digits ? digits.join('') : '';
      }
  }

  search(){
    this.searchData(this.term);
    this.router.navigate(['/app/search'], {queryParams: {search: this.term}, queryParamsHandling: 'merge'})
  }

  handleSearch() {
    if (!this.term) {
      this._shared.customPopups('Enter a value to search', 2);
      return;
    }

    // Pure sales agents / team leads -> only the sales leads API.
    if (this.searchBySalesAgent) {
      this.fetchSalesLeads().subscribe(res => this.applySalesLeads(res));
      return;
    }

    // Sales managers -> both APIs together (merge admin sections + sales leads).
    if (this.isManager) {
      forkJoin({
        admin: this.fetchAdminSearch(),
        leads: this.fetchSalesLeads(),
      }).subscribe(({ admin, leads }) => {
        // Suppress the admin "no results" popup here — for a manager, leads may
        // still exist; decide the combined empty-state after both are applied.
        this.applyAdminSearch(admin, false);
        this.applySalesLeads(leads, false);

        const hasLeads = this.filteredUsers.length > 0;
        this.emptyData = !this.hasAdminResults() && !hasLeads;
        if (this.emptyData) {
          this._shared.customPopups('No results found!', 1);
        } else {
          this.searched_string = this.term;
        }
      });
      return;
    }

    // Everyone else (admins) -> only the dashboard search API.
    this.fetchAdminSearch().subscribe(res => this.applyAdminSearch(res));
  }

  /** dashboard/search — partners, doctors, patients, contracts, payments, etc. */
  private fetchAdminSearch(): Observable<any> {
    const data = { search_string: this.term };
    return this._api.api(data, 'dashboard/search', 'get');
  }

  /** sales/search-lead — sales leads list, scoped by the active dashboard view. */
  private fetchSalesLeads(): Observable<any> {
    const data: any = {
      search_string: this.term,
      timezone: this._shared.getTimezoneZone() || 'America/Los_Angeles',
    };

    if (this.isTeamLead || this.isManager) {
      data.view_as = this.salesView?.dashboardView ? this.salesView?.dashboardView : 'my';
    }

    if (this.isTeamLead && this.salesView.effectiveSelectedAdminId != null) {
      data.selected_admin_id = this.salesView.effectiveSelectedAdminId;
    }

    return this._api.api(data, 'sales/search-lead', 'get');
  }

  private applyAdminSearch(res: any, evaluateEmptyState = true) {
    this.results.partners = res.partner_list;
    this.results.doctors = res.doctor_list;
    this.results.patients = res.patient_list;
    this.results.contracts = res.pf_payments;
    this.results.payments = res.payments_list;
    this.results.ar = res.ar_list;
    this.results.proposal = res.proposal_list;
    this.results.pa_leads = res.pa_leads;
    this.results.training = res.training_list;
    this.totalRowsOfEach = res.totalRowsOfEach;

    // Managers merge these results with sales leads, so they own the combined
    // empty-state decision themselves.
    if (!evaluateEmptyState) {
      return;
    }

    if (this.hasAdminResults()) {
      this.searched_string = this.term;
      this.emptyData = false;
    } else {
      this.emptyData = true;
      this._shared.customPopups('No results found!', 1);
    }
  }

  private hasAdminResults(): boolean {
    return Object.keys(this.results).some(
      key => this.results[key] && this.results[key].length
    );
  }

  private applySalesLeads(res: any, evaluateEmptyState = true) {
    // sales/search-lead now returns a paginated payload:
    // { data: { filteredLeads: [...], number_of_records, number_of_pages, records } }
    this.users = res.data?.filteredLeads || [];
    this.filteredUsers = this.users;
    this.resultCount = res.data?.number_of_records ?? this.filteredUsers.length;
    if (this.term) {
      this.searchData(this.term);
    }

    // Managers merge leads with admin results and own the combined empty-state.
    if (!evaluateEmptyState) {
      return;
    }

    if (this.filteredUsers.length) {
      this.searched_string = this.term;
      this.emptyData = false;
    } else {
      this.emptyData = true;
    }
  }
  viewPartner(partner_id: any) {
    // MainService.customSetItem("partnerList", partner_id);
    this.router.navigate(['app', 'partners', 'profile', partner_id]);
  }

  viewAllDetails(search_type:any){
    this.router.navigate(['/app/details-search'], {queryParams: {search: this.term , search_type:search_type}, queryParamsHandling: 'merge'})
  }

  viewAllLeads(){
    this.router.navigate(['/app/details-search'], {queryParams: {search: this.term, search_type: 'leads'}, queryParamsHandling: 'merge'})
  }

  goToLeadDetails(user: any) {
    // Was: role_enum === 'Team_lead_l1' -> sales_l1_id, 'Team_lead_l2' -> sales_l2_id.
    // Cap-driven: TL on L1 dashboard -> L1 assignee; TL on L2 dashboard -> L2 assignee.
    let admin_id: number | null = null;
    if (this.isTeamLeadL1) {
      admin_id = user.sales_l1_id;
    } else if (this.isTeamLeadL2) {
      admin_id = user.sales_l2_id;
    }
    this.router.navigate(['/app/sales/my-leads-details'], {
      queryParams: { lead_id: user.lead_id, admin_id }
    });
  }

  navigateToDetails(lead: any) {
    const queryParams: any = { lead_id: lead.lead_id , doctor_id: lead.doctor_id};
    // Legacy logic: agents and TLs at a given tier forward sales_l{N}_id;
    // managers forward based on the active dashboardView toggle.
    if ((this.isTeamLeadL1 || this.isAgentL1Pure) && lead?.sales_l1_id != null) {
        queryParams.admin_id = lead.sales_l1_id;
    } else if ((this.isTeamLeadL2 || this.isAgentL2Pure) && lead?.sales_l2_id != null) {
        queryParams.admin_id = lead.sales_l2_id;
    } else if (this.isManager && this.salesView.dashboardView === 'L1') {
        queryParams.admin_id = lead.sales_l1_id;
    } else if (this.isManager && this.salesView.dashboardView === 'L2') {
        queryParams.admin_id = lead.sales_l2_id;
    }
    else if (this.isManager && this.salesView.dashboardView === 'L1') {
        queryParams.admin_id = lead.sales_l1_id;
    } else if (this.isManager && this.salesView.dashboardView === 'L2') {
        queryParams.admin_id = lead.sales_l2_id;
    }
    this.router.navigate(['app/sales/my-leads-details'], { queryParams });
}

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.permissionsDestroy$.next();
    this.permissionsDestroy$.complete();
  }
}
